<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { INSTRUCTION_TEXTS } from "@/utils/constant";
import StakingInfoPanel from "@/components/stake/StakingInfoPanel.vue";
import NexusTab from "@/components/stake/NexusTab.vue";
import RewardsPanel from "@/components/stake/RewardsPanel.vue";
import TransferFromOldWalletDialog from "@/components/Dialog/TransferFromOldWalletDialog.vue";
import { useDojoStakingStore } from "@/plugins/2.pinia/modules/nfts/dojoStakingStore";
import { useStakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/stakedVrumblersStore";
import { useUnstakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/unstakedVrumblersStore";
import { useAuthStore } from "@/plugins/2.pinia/modules/auth";
import { useDisplay } from "vuetify";

definePage({
  name: "StakeMobileNexus",
  path: "/stake/mobile-nexus",
  meta: {
    layout: "default",
    requiresAuth: true,
    title: "Stake NEXUS",
    description: "Stake for $NEXUS rewards and monitor staked Vrumblers.",
  },
});

const dojoStakingStore = useDojoStakingStore();
const stakedStore = useStakedVrumblersStore();
const unstakedVrumblersStore = useUnstakedVrumblersStore();
const onboarding = useOnboardingStore();
const authStore = useAuthStore();
const router = useRouter();
const { mobile } = useDisplay();

const activeTab = ref("$NEXUS");
const showTransferDialog = ref(false);

const rewards = ref({
  $SUJI: 0,
  $NEXUS: 0,
});

const unstakedCount = computed(
  () => unstakedVrumblersStore.unstakedVrumblersCount
);

const stakedCount = computed(
  () => stakedStore.stakedVrumblersArray?.length || 0
);

const handleOpenTransferDialog = () => {
  console.log("Opening old wallet transfer dialog");
  showTransferDialog.value = true;
};

const fetchNftData = async () => {
  try {
    await Promise.all([
      unstakedVrumblersStore.fetchUnstakedVrumblers(),
      stakedStore.fetchStakedVrumblers(),
    ]);

    console.log(
      `Loaded ${unstakedCount.value} unstaked NFTs and ${stakedCount.value} staked NFTs`
    );
  } catch (error) {
    console.error("Failed to load NFT data:", error);

    if (
      error.message?.includes("No wallet connected") ||
      error.message?.includes("Wallet authorization") ||
      error.message?.includes("Local Key not found")
    ) {
      dojoStakingStore.handleAuthorizationFailure();
    }
  }
};

const handleTransferComplete = async (transferredTokenIds) => {
  console.log("Transfer completed with token IDs:", transferredTokenIds);

  console.log(
    `Successfully transferred ${transferredTokenIds.length} Vrumblers to your account`
  );

  await fetchNftData();
};

watch(
  () => dojoStakingStore.stakingError,
  (error) => {
    if (error) {
      console.error("Staking error from store:", error);
    }
  }
);

watch(
  () => dojoStakingStore.unstakingError,
  (error) => {
    if (error) {
      console.error("Unstaking error from store:", error);
    }
  }
);

watch(
  () => authStore.isConnectedPN,
  (isConnected) => {
    if (isConnected) {
      fetchNftData();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (!mobile.value) {
    await router.push("/stake");
    return;
  }

  await nextTick();
  await fetchNftData();

  if (!onboarding.shown.stake) {
    onboarding.showInstruction({
      text: INSTRUCTION_TEXTS.stake,
      onClose: () => onboarding.markShown("stake"),
    });
  }
});
</script>

<template>
  <div>
    <!-- StarrySky as fixed background -->
    <StarrySky class="" />

    <div class="tw-page-container tw-min-h-screen tw-relative tw-z-10">
      <v-container>
        <!-- Main two-column layout -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1fr_3fr] tw-gap-6">
          <!-- Left Column - Staking Info -->
          <StakingInfoPanel
            @open-old-wallet-dialog="handleOpenTransferDialog"
          />

          <!-- Right Column - Tabs and Content -->
          <div class="tw-flex tw-flex-col">
            <!-- Main Tabs Component -->

            <!-- Staking Tabs (only shown for MYSTICS tab) -->
            <div class="tw-h-[50px]">
              <!-- Empty placeholder when on $NEXUS tab -->
              <div class="tw-h-full"></div>
            </div>

            <!-- Two-column sub-layout for the content area -->
            <div
              class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[2fr_1fr] tw-gap-6"
            >
              <!-- Left sub-column - NFT Display -->
              <div class="tw-flex tw-flex-col tw-gap-4">
                <!-- NFT Display -->
                <div class="tw-rounded-lg tw-p-0 tw-flex-1">
                  <NexusTab :rewards="rewards" />
                </div>
              </div>
              <RewardsPanel
                :activeTab="activeTab"
                :rewards="rewards"
                :stakedMysticsCount="stakedCount"
              />
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <!-- OLD WALLET TRANSFER DIALOG -->
    <TransferFromOldWalletDialog
      v-model="showTransferDialog"
      @transfer-complete="handleTransferComplete"
    />
  </div>
</template>
